import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { ChevronDownIcon, CopyIcon, SearchIcon, XIcon } from 'lucide-react';
import useI18n from '../../hooks/useI18n';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { Label } from '../Label';
import {
  floatingFieldLabelClassName,
  floatingFieldLabelInsetClassName,
} from '../Label/helpers';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../InputGroup';
import { useComboboxAnchor, useComboboxFilteredItems } from './hooks';
import {
  copyTextToClipboard,
  getComboboxItemLabel,
  getComboboxItemKey,
  hasSelectedComboboxValue,
  getComboboxGroupKey,
} from './helpers';
import type {
  ComboboxChipProps,
  ComboboxChipsProps,
  ComboboxClearProps,
  ComboboxCollectionProps,
  ComboboxContentProps,
  ComboboxEmptyProps,
  ComboboxGroupedItem,
  ComboboxGroupedProps,
  ComboboxGroupLabelProps,
  ComboboxGroupProps,
  ComboboxInputProps,
  ComboboxItemProps,
  ComboboxLabelProps,
  ComboboxListProps,
  ComboboxMultipleProps,
  ComboboxProps,
  ComboboxRootProps,
  ComboboxSeparatorProps,
  ComboboxTriggerButtonProps,
  ComboboxTriggerIndicatorProps,
  ComboboxTriggerProps,
} from './types';

/**
 * Groups all parts of the combobox. Doesn’t render its own HTML element.
 *
 * API:
 * - [Root API](https://base-ui.com/react/components/combobox#root)
 */
function ComboboxRoot<Value, Multiple extends boolean | undefined = false>({
  ...props
}: ComboboxRootProps<Value, Multiple>) {
  return <ComboboxPrimitive.Root {...props} />;
}

/**
 * An accessible label that is automatically associated with the combobox trigger.
 *
 * API:
 * - [Label API](https://base-ui.com/react/components/combobox#label)
 */
function ComboboxLabel({
  className,
  placement = 'inside',
  ...props
}: ComboboxLabelProps) {
  return (
    <Label
      data-slot='combobox-label'
      className={cn(
        // Base styles
        floatingFieldLabelClassName,
        'eye:mb-4',
        // Inside placement
        placement === 'inside' &&
          cn(floatingFieldLabelInsetClassName, 'eye:top-0'),
        // Outside placement
        placement === 'outside' &&
          'eye:group-has-disabled/combobox-field:text-grey-disabled eye:group-has-aria-invalid/combobox-field:text-red-100',
        // Disabled states
        'eye:group-has-disabled/combobox-trigger:text-grey-disabled',
        'eye:group-has-disabled/input-group:text-grey-disabled',
        // Validation states
        'eye:group-has-aria-invalid/combobox-trigger:text-red-100',
        'eye:group-has-[[data-slot=input-group-control][aria-invalid=true]]/input-group:text-red-100',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The current value of the combobox.
 *
 * API:
 * - [Value API](https://base-ui.com/react/components/combobox#value)
 */
function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot='combobox-value' {...props} />;
}

/**
 * A wrapper for the trigger button that contains the input and auxiliary
 * controls such as the clear and copy buttons. Should contain
 * `ComboboxTriggerButton` as the main interactive element that opens the popup.
 */
const ComboboxTrigger = React.forwardRef<HTMLDivElement, ComboboxTriggerProps>(
  (
    {
      className,
      children,
      disabled = false,
      showClear = false,
      showCopy = false,
      style,
      ...props
    },
    ref,
  ) => {
    const { t } = useI18n();
    let padding = '52px';

    if (showClear && showCopy) {
      padding = '108px';
    } else if (showClear || showCopy) {
      padding = '80px';
    }

    return (
      <InputGroup
        ref={ref}
        data-slot='combobox-trigger'
        className={cn('eye:group/combobox-trigger', className)}
        style={
          {
            ...style,
            '--combobox-trigger-end-padding': padding,
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
        <InputGroupAddon
          align='inline-end'
          className='eye:pointer-events-none eye:absolute eye:inset-y-0 eye:right-0 eye:z-10 eye:cursor-default eye:gap-4 eye:self-stretch eye:pr-16!'
        >
          {showClear && <ComboboxClear aria-label={t('clearValue')} />}
          {showCopy && <ComboboxTriggerCopy disabled={disabled} />}
          <ComboboxTriggerIndicator />
        </InputGroupAddon>
      </InputGroup>
    );
  },
);

ComboboxTrigger.displayName = 'ComboboxTrigger';

/**
 * A button that copies the current value to the clipboard. Should be used within `ComboboxTrigger`.
 */
function ComboboxTriggerCopy({
  className,
  onClick,
  onMouseDown,
  onPointerDown,
  ...props
}: React.ComponentPropsWithoutRef<typeof Button>) {
  const { t } = useI18n();

  return (
    <ComboboxValue>
      {(selectedValue: unknown) => {
        if (!hasSelectedComboboxValue(selectedValue)) {
          return null;
        }

        return (
          <Tooltip content={t('copyToClipboard')} delayDuration={1500}>
            <Button
              variant='ghost'
              size='icon-xs'
              type='button'
              data-slot='combobox-trigger-copy'
              aria-label={t('copyToClipboard')}
              className={cn(
                'eye:group/combobox-trigger-copy eye:pointer-events-auto eye:shrink-0 eye:cursor-pointer eye:hover:bg-grey-20 eye:disabled:cursor-not-allowed eye:disabled:bg-transparent eye:disabled:text-grey-disabled eye:disabled:hover:bg-transparent eye:data-pressed:bg-transparent',
                className,
              )}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPointerDown?.(event);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onMouseDown?.(event);
              }}
              onClick={async (event) => {
                event.stopPropagation();

                const triggerButton = event.currentTarget
                  .closest('[data-slot="combobox-trigger"]')
                  ?.querySelector('[data-slot="combobox-trigger-button"]');
                const text = triggerButton?.textContent?.trim() ?? '';

                try {
                  await copyTextToClipboard(text);
                } finally {
                  onClick?.(event);
                }
              }}
              {...props}
            >
              <CopyIcon
                data-slot='combobox-trigger-copy-icon'
                className='eye:pointer-events-none eye:size-16 eye:shrink-0'
              />
            </Button>
          </Tooltip>
        );
      }}
    </ComboboxValue>
  );
}

/**
 * A button that opens the popup. Should be used within `ComboboxTrigger` as the main interactive element that opens the popup.
 *
 * API:
 * - [Trigger API](https://base-ui.com/react/components/combobox#trigger)
 */
const ComboboxTriggerButton = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerButtonProps
>(({ className, children, style, ...props }, ref) => {
  return (
    <ComboboxPrimitive.Trigger
      ref={ref}
      render={<button type='button' />}
      data-slot='combobox-trigger-button'
      className={cn(
        'eye:group/combobox-trigger-button eye:min-h-46 eye:flex eye:min-w-0 eye:flex-1 eye:cursor-pointer eye:items-center eye:gap-8 eye:self-stretch eye:border-none eye:bg-transparent eye:py-0 eye:pr-16 eye:pl-16 eye:text-left eye:text-16 eye:text-black eye:outline-none eye:disabled:cursor-not-allowed eye:disabled:text-grey-disabled',
        'eye:has-data-[slot=combobox-chip]:pt-12 eye:has-data-[slot=combobox-chip]:pb-8',
        className,
      )}
      style={
        {
          paddingRight: 'var(--combobox-trigger-end-padding, 16px)',
          ...style,
        } as React.CSSProperties
      }
      {...props}
    >
      <span className='eye:flex eye:min-w-0 eye:flex-1 eye:items-center eye:gap-8'>
        {children}
      </span>
    </ComboboxPrimitive.Trigger>
  );
});

ComboboxTriggerButton.displayName = 'ComboboxTriggerButton';

/**
 * A decorative chevron rendered inside the trigger action area.
 * The actual toggle interaction stays on the single Base UI trigger button.
 */
function ComboboxTriggerIndicator({
  className,
  ...props
}: ComboboxTriggerIndicatorProps) {
  return (
    <span
      data-slot='combobox-trigger-indicator'
      aria-hidden='true'
      className={cn(
        'eye:pointer-events-none eye:flex eye:size-24 eye:shrink-0 eye:items-center eye:justify-center eye:rounded-[3px]',
        className,
      )}
      {...props}
    >
      <ChevronDownIcon
        data-slot='combobox-trigger-icon'
        className='eye:pointer-events-none eye:size-20 eye:shrink-0 eye:text-grey-120 eye:transition-transform eye:duration-150 eye:group-has-disabled/combobox-trigger:text-grey-disabled eye:group-has-data-popup-open/combobox-trigger:rotate-180'
      />
    </span>
  );
}

/**
 * A container for the chips in a multiselectable input
 *
 * API:
 * - [Chips API](https://base-ui.com/react/components/combobox#chips)
 */
const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxChipsProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Chips
        ref={ref}
        data-slot='combobox-chips'
        className={cn(
          'eye:flex eye:max-w-full eye:min-w-0 eye:flex-wrap eye:gap-[6px]',
          className,
        )}
        {...props}
      >
        {children}
      </ComboboxPrimitive.Chips>
    );
  },
);

ComboboxChips.displayName = 'ComboboxChips';

/**
 * An individual chip that represents a value in a multiselectable input.
 */
function ComboboxChip({ className, ...props }: ComboboxChipProps) {
  return (
    <span
      data-slot='combobox-chip'
      className={cn(
        'eye:body-s eye:flex eye:min-h-24 eye:max-w-full eye:min-w-0 eye:items-center eye:gap-4 eye:rounded-md eye:border eye:border-grey-40 eye:bg-grey-20 eye:p-[2px] eye:px-12 eye:wrap-break-word eye:whitespace-normal',
        className,
      )}
      {...props}
    />
  );
}

/**
 * API:
 * - [Clear API](https://base-ui.com/react/components/combobox#clear)
 */
function ComboboxClear({ className, ...props }: ComboboxClearProps) {
  return (
    <ComboboxPrimitive.Clear
      data-slot='combobox-clear'
      render={<Button variant='ghost' size='icon-xs' />}
      className={cn('eye:pointer-events-auto', className)}
      {...props}
    >
      <XIcon className='eye:pointer-events-none eye:size-16' />
    </ComboboxPrimitive.Clear>
  );
}

/**
 * API:
 * - [InputGroup API](https://base-ui.com/react/components/combobox#input-group)
 * - [Input API](https://base-ui.com/react/components/combobox#input)
 */
function ComboboxInput({
  className,
  placeholder,
  ...props
}: ComboboxInputProps) {
  const { t } = useI18n();

  return (
    <ComboboxPrimitive.InputGroup
      render={<InputGroup />}
      data-slot='combobox-input-group'
      className={cn(
        'eye:rounded-b-none eye:border-0! eye:border-b! eye:border-b-grey-40! eye:outline-none! eye:focus-within:border-b-grey-40! eye:hover:border-b-grey-40!',
        'eye:*:data-[slot=input-group-control]:px-16',
        'eye:group-data-closed/combobox-content:**:data-[slot=input-group-control]:text-transparent eye:group-data-closed/combobox-content:**:data-[slot=input-group-control]:caret-transparent',
        className,
      )}
    >
      <InputGroupAddon
        align='inline-start'
        className='eye:pointer-events-none eye:cursor-default'
      >
        <SearchIcon
          data-slot='combobox-input-icon'
          className='eye:size-16 eye:text-grey-100'
        />
      </InputGroupAddon>
      <ComboboxPrimitive.Input
        render={<InputGroupInput className='eye:px-16' />}
        placeholder={placeholder ?? t('searchPlaceholder')}
        {...props}
      />
    </ComboboxPrimitive.InputGroup>
  );
}

/**
 * API:
 * - [Portal API](https://base-ui.com/react/components/combobox#portal)
 * - [Positioner API](https://base-ui.com/react/components/combobox#positioner)
 * - [Popup API](https://base-ui.com/react/components/combobox#popup)
 */
function ComboboxContent({
  className,
  container,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxContentProps) {
  return (
    // container defaults to document.body, override this when using the
    // Combobox within, for example, a Modal. Alternatively override the
    // z-index via slotProps to ensure the popup appears above other content.
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className='eye:isolate eye:z-50'
      >
        <ComboboxPrimitive.Popup
          data-slot='combobox-content'
          className={cn(
            // Layout and sizing
            'eye:group/combobox-content eye:relative eye:z-60 eye:box-border',
            'eye:w-(--anchor-width) eye:overflow-hidden',
            // Surface
            'eye:rounded-md eye:border eye:border-grey-40 eye:bg-white eye:text-black',
            'eye:shadow-popover-content',
            // Motion
            'eye:duration-100 eye:data-closed:animate-out eye:data-closed:fade-out-0 eye:data-closed:zoom-out-95',
            'eye:data-open:animate-in eye:data-open:fade-in-0 eye:data-open:zoom-in-95',
            'eye:data-[side=bottom]:slide-in-from-top-2 eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2 eye:data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

/**
 * API:
 * - [List API](https://base-ui.com/react/components/combobox#list)
 */
function ComboboxList({ className, ...props }: ComboboxListProps) {
  return (
    <ComboboxPrimitive.List
      data-slot='combobox-list'
      className={cn(
        'eye:max-h-[324px] eye:scroll-py-4 eye:overflow-y-auto eye:py-4xs eye:data-empty:p-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * API:
 * - [Item API](https://base-ui.com/react/components/combobox#item)
 */
const ComboboxItem = React.forwardRef<HTMLDivElement, ComboboxItemProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Item
        ref={ref}
        data-slot='combobox-item'
        className={cn(
          // Layout
          'eye:flex eye:min-h-[44px] eye:w-full eye:items-center eye:gap-8 eye:border-0 eye:px-16 eye:py-[6px]',
          // Typography and transitions
          'eye:text-14 eye:text-black eye:transition-[background-color,color] eye:duration-150',
          // Interaction
          'eye:cursor-pointer eye:outline-none eye:select-none',
          'eye:hover:bg-grey-10 eye:hover:text-black eye:focus:outline-none eye:focus-visible:outline-none',
          // Stateful variants
          'eye:data-disabled:pointer-events-none eye:data-disabled:opacity-50',
          'eye:data-highlighted:bg-grey-10 eye:data-highlighted:text-black',
          'eye:data-selected:bg-grey-20 eye:data-selected:text-black eye:data-selected:hover:bg-grey-40',
          'eye:[&[data-selected][data-highlighted]]:bg-grey-20 eye:[&[data-selected][data-highlighted]]:text-black eye:[&[data-selected][data-highlighted]:hover]:bg-grey-40',
          className,
        )}
        {...props}
      >
        {children}
      </ComboboxPrimitive.Item>
    );
  },
);

ComboboxItem.displayName = 'ComboboxItem';

/**
 * API:
 * - [Group API](https://base-ui.com/react/components/combobox#group)
 */
function ComboboxGroup({ className, ...props }: ComboboxGroupProps) {
  return (
    <ComboboxPrimitive.Group
      data-slot='combobox-group'
      className={cn(className)}
      {...props}
    />
  );
}

/**
 * API:
 * - [GroupLabel API](https://base-ui.com/react/components/combobox#group-label)
 */
function ComboboxGroupLabel({ className, ...props }: ComboboxGroupLabelProps) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot='combobox-group-label'
      className={cn(
        'eye:px-16 eye:py-[6px] eye:text-12 eye:text-grey-100!',
        className,
      )}
      {...props}
    />
  );
}

/**
 * API:
 * - [Collection API](https://base-ui.com/react/components/combobox#collection)
 */
function ComboboxCollection({ ...props }: ComboboxCollectionProps) {
  return (
    <ComboboxPrimitive.Collection data-slot='combobox-collection' {...props} />
  );
}

/**
 * API:
 * - [Empty API](https://base-ui.com/react/components/combobox#empty)
 */
function ComboboxEmpty({ children, className, ...props }: ComboboxEmptyProps) {
  const { t } = useI18n();

  return (
    <ComboboxPrimitive.Empty
      data-slot='combobox-empty'
      className={cn(
        'eye:body-m eye:hidden eye:min-h-[44px] eye:w-full eye:items-center eye:justify-center eye:text-grey-disabled eye:group-data-empty/combobox-content:flex',
        className,
      )}
      {...props}
    >
      {children ?? t('noResultsFound')}
    </ComboboxPrimitive.Empty>
  );
}

/**
 * API:
 * - [Separator API](https://base-ui.com/react/components/combobox#separator)
 */
function ComboboxSeparator({ className, ...props }: ComboboxSeparatorProps) {
  return (
    <ComboboxPrimitive.Separator
      data-slot='combobox-separator'
      className={cn('eye:-mx-4 eye:my-4 eye:h-px eye:bg-grey-20', className)}
      {...props}
    />
  );
}

/**
 * A list container for grouped items.
 */
function ComboboxGroupedList<Value>({
  itemToStringLabel,
  itemToStringValue,
  renderGroupLabel,
  renderItemLabel,
  slotProps,
}: {
  itemToStringLabel: ComboboxGroupedProps<Value>['itemToStringLabel'];
  itemToStringValue: ComboboxGroupedProps<Value>['itemToStringValue'];
  renderGroupLabel: ComboboxGroupedProps<Value>['renderGroupLabel'];
  renderItemLabel: ComboboxGroupedProps<Value>['renderItemLabel'];
  slotProps: ComboboxGroupedProps<Value>['slotProps'];
}) {
  const filteredGroups = useComboboxFilteredItems<ComboboxGroupedItem<Value>>();
  const visibleGroupCount = filteredGroups.length;

  return (
    <ComboboxList>
      {(group: ComboboxGroupedItem<Value>, groupIndex: number) => {
        const groupKey = getComboboxGroupKey(group, groupIndex);

        return (
          <ComboboxGroup
            {...slotProps?.group}
            key={groupKey}
            items={group.items}
          >
            <ComboboxGroupLabel {...slotProps?.groupLabel}>
              {renderGroupLabel?.(group) ?? group.value}
            </ComboboxGroupLabel>
            <ComboboxCollection>
              {(item: Value, itemIndex: number) => (
                <ComboboxItem
                  {...slotProps?.item}
                  key={`${groupKey}-${getComboboxItemKey(
                    item,
                    itemIndex,
                    itemToStringValue,
                  )}`}
                  value={item}
                >
                  {renderItemLabel?.(item) ??
                    getComboboxItemLabel(item, itemToStringLabel)}
                </ComboboxItem>
              )}
            </ComboboxCollection>
            {groupIndex < visibleGroupCount - 1 && (
              <ComboboxSeparator {...slotProps?.separator} />
            )}
          </ComboboxGroup>
        );
      }}
    </ComboboxList>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports single selection mode. Use `ComboboxMultiple` for multiple selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function Combobox<Value>({
  disabled = false,
  itemToStringLabel,
  itemToStringValue,
  label,
  noItemsText,
  renderItemLabel,
  required,
  slotProps,
  ...rest
}: ComboboxProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';

  return (
    <ComboboxRoot<Value>
      disabled={disabled}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger ref={anchor} disabled={disabled} {...triggerProps}>
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={triggerAriaLabelledby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value | null) => (
                <span
                  className={cn(
                    'eye:truncate',
                    !selectedValue && 'eye:text-grey-placeholder',
                  )}
                >
                  {selectedValue
                    ? getComboboxItemLabel(selectedValue, itemToStringLabel)
                    : slotProps?.value?.placeholder}
                </span>
              )}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxList>
          <ComboboxCollection>
            {(item: Value, index: number) => (
              <ComboboxItem
                {...slotProps?.item}
                key={getComboboxItemKey(item, index, itemToStringValue)}
                value={item}
              >
                {renderItemLabel?.(item) ??
                  getComboboxItemLabel(item, itemToStringLabel)}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports grouped single selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function ComboboxGrouped<Value>({
  disabled = false,
  itemToStringLabel,
  itemToStringValue,
  label,
  noItemsText,
  required,
  renderGroupLabel,
  renderItemLabel,
  slotProps,
  ...rest
}: ComboboxGroupedProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';

  return (
    <ComboboxRoot<Value>
      disabled={disabled}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger ref={anchor} disabled={disabled} {...triggerProps}>
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={triggerAriaLabelledby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value | null) => (
                <span
                  className={cn(
                    'eye:truncate',
                    !selectedValue && 'eye:text-grey-placeholder',
                  )}
                >
                  {selectedValue
                    ? getComboboxItemLabel(selectedValue, itemToStringLabel)
                    : slotProps?.value?.placeholder}
                </span>
              )}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxGroupedList
          itemToStringLabel={itemToStringLabel}
          itemToStringValue={itemToStringValue}
          renderGroupLabel={renderGroupLabel}
          renderItemLabel={renderItemLabel}
          slotProps={slotProps}
        />
      </ComboboxContent>
    </ComboboxRoot>
  );
}

/**
 * A light wrapper around the composable Base UI Combobox components that supports multiple selection mode. Use `Combobox` for single selection mode.
 *
 * Demo:
 * - [Combobox](https://base-ui.com/react/components/combobox)
 */
function ComboboxMultiple<Value>({
  disabled = false,
  label,
  noItemsText,
  renderChipLabel,
  renderItemLabel,
  required,
  slotProps,
  itemToStringLabel,
  itemToStringValue,
  ...rest
}: ComboboxMultipleProps<Value>) {
  const anchor = useComboboxAnchor();
  const generatedTriggerId = React.useId();

  const {
    id: triggerIdProp,
    'aria-label': triggerAriaLabel,
    'aria-labelledby': triggerAriaLabelledby,
    ...triggerProps
  } = slotProps?.trigger ?? {};

  const triggerId = triggerIdProp ?? generatedTriggerId;
  const hasLabel = label != null;
  const labelPlacement = slotProps?.label?.placement ?? 'inside';

  return (
    <ComboboxRoot<Value, true>
      disabled={disabled}
      required={required}
      itemToStringLabel={itemToStringLabel}
      itemToStringValue={itemToStringValue}
      multiple
      autoHighlight
      {...rest}
    >
      <div className='eye:group/combobox-field eye:block eye:w-full'>
        {hasLabel && labelPlacement === 'outside' && (
          <ComboboxLabel
            {...slotProps?.label}
            htmlFor={triggerId}
            placement='outside'
          >
            {required ? `${label} *` : label}
          </ComboboxLabel>
        )}
        <ComboboxTrigger ref={anchor} disabled={disabled} {...triggerProps}>
          {hasLabel && labelPlacement === 'inside' && (
            <ComboboxLabel
              {...slotProps?.label}
              htmlFor={triggerId}
              placement='inside'
            >
              {required ? `${label} *` : label}
            </ComboboxLabel>
          )}
          <ComboboxTriggerButton
            id={triggerId}
            aria-invalid={triggerProps['aria-invalid']}
            aria-label={triggerAriaLabel}
            aria-labelledby={triggerAriaLabelledby}
          >
            <ComboboxValue {...slotProps?.value}>
              {(selectedValue: Value[] | null) => {
                if (!selectedValue?.length) {
                  return (
                    <span className='eye:truncate eye:text-grey-placeholder'>
                      {slotProps?.value?.placeholder}
                    </span>
                  );
                }

                return (
                  <ComboboxChips>
                    {selectedValue.map((selectedItem, index) => (
                      <ComboboxChip
                        {...slotProps?.chip}
                        key={getComboboxItemKey(
                          selectedItem,
                          index,
                          itemToStringValue,
                        )}
                      >
                        {renderChipLabel?.(selectedItem) ??
                          getComboboxItemLabel(selectedItem, itemToStringLabel)}
                      </ComboboxChip>
                    ))}
                  </ComboboxChips>
                );
              }}
            </ComboboxValue>
          </ComboboxTriggerButton>
        </ComboboxTrigger>
      </div>
      <ComboboxContent anchor={anchor} {...slotProps?.content}>
        <ComboboxInput {...slotProps?.searchInput} />
        <ComboboxEmpty {...slotProps?.empty}>{noItemsText}</ComboboxEmpty>
        <ComboboxList>
          <ComboboxCollection>
            {(item: Value, index: number) => (
              <ComboboxItem
                {...slotProps?.item}
                key={getComboboxItemKey(item, index, itemToStringValue)}
                value={item}
              >
                {renderItemLabel?.(item) ??
                  getComboboxItemLabel(item, itemToStringLabel)}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGrouped,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxMultiple,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxTriggerIndicator,
  ComboboxValue,
};
